<template>
  <VBusHelper
    :reactOn="constants.EVENTS.DELIVERYBOOK.REQUEST_MODAL_OPEN"
    :handler="showModal"
  />

  <DialogComponent
    ref="modal"
    dialogId="deliverybook-request-dialog"
    type="bottomsheet"
    noInnerPadding
    @closed="resetForm"
  >
    <div class="deliverybook-request-dialog">
      <!-- Header -->
      <div
        class="px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10"
      >
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-xl font-bold text-slate-800">
              Demander une adresse
            </h2>
            <p class="text-sm text-slate-500 mt-1">
              Invitez quelqu'un a partager son adresse
            </p>
          </div>
          <button
            class="w-10 h-10 flex items-center justify-center rounded-2xl bg-slate-100 hover:bg-slate-200 transition-all duration-300"
            @click="hideModal"
          >
            <svg
              class="w-5 h-5 text-slate-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              stroke-width="2.5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Info Box -->
      <div class="p-6 pb-0">
        <div
          class="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex gap-3"
        >
          <div
            class="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0"
          >
            <svg
              class="w-5 h-5 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="flex-1">
            <p class="text-sm text-blue-800 font-medium">
              Un lien sera genere que vous pourrez envoyer a votre contact pour
              qu'il renseigne son adresse.
            </p>
          </div>
        </div>
      </div>

      <!-- Success Message with Link Copy (shown after request created) -->
      <div v-if="generatedLink" class="p-6 pb-0">
        <div class="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
          <div class="flex items-start gap-3">
            <div
              class="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0"
            >
              <svg
                class="w-5 h-5 text-emerald-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div class="flex-1">
              <p class="text-sm text-emerald-800 font-medium mb-3">
                Demande creee ! Copiez le lien ci-dessous pour l'envoyer a votre
                contact.
              </p>
              <div class="flex gap-2">
                <input
                  :value="generatedLink"
                  readonly
                  class="flex-1 px-3 py-2 bg-white border border-emerald-200 rounded-lg text-xs text-emerald-700"
                />
                <button
                  class="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors"
                  @click="copyLink"
                >
                  Copier
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Form (hidden after success) -->
      <div v-if="!generatedLink" class="p-6 space-y-4">
        <!-- Name -->
        <div>
          <label class="block text-sm font-semibold text-slate-700 mb-2">
            Nom du contact *
          </label>
          <input
            v-model="form.name"
            type="text"
            placeholder="Ex: Jean Dupont"
            class="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
            :class="{ 'border-red-300': errors.name }"
          />
          <p v-if="errors.name" class="text-red-500 text-xs mt-1">
            {{ errors.name }}
          </p>
        </div>

        <!-- Pseudo (optional) -->
        <div>
          <label class="block text-sm font-semibold text-slate-700 mb-2">
            Pseudo (optionnel)
          </label>
          <input
            v-model="form.pseudo"
            type="text"
            placeholder="Ex: Maman Sami de Godomey"
            class="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
          />
        </div>

        <!-- Phone -->
        <div>
          <label class="block text-sm font-semibold text-slate-700 mb-2">
            Telephone *
          </label>
          <div class="relative">
            <span
              class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold"
            >
              +229
            </span>
            <input
              v-model="form.phoneNumber"
              type="tel"
              placeholder="01 23 45 67 89"
              class="w-full pl-16 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
              :class="{ 'border-red-300': errors.phoneNumber }"
            />
          </div>
          <p v-if="errors.phoneNumber" class="text-red-500 text-xs mt-1">
            {{ errors.phoneNumber }}
          </p>
        </div>
      </div>

      <!-- Submit Button (hidden after success) -->
      <div v-if="!generatedLink" class="p-6 pt-0">
        <button
          class="w-full py-4 bg-gradient-to-r from-orange-500 to-primary-500 text-white rounded-2xl font-bold shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isSubmitting"
          @click="submitForm"
        >
          <span v-if="!isSubmitting">Creer la demande</span>
          <span v-else>Creation...</span>
        </button>
      </div>

      <!-- Close button (shown after success) -->
      <div v-else class="p-6 pt-0">
        <button
          class="w-full py-4 bg-slate-200 text-slate-700 rounded-2xl font-bold hover:bg-slate-300 transition-all duration-300"
          @click="closeModal"
        >
          Fermer
        </button>
      </div>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';

const modal = ref(null);

// State
const isSubmitting = ref(false);
const generatedLink = ref('');
const form = reactive({
  name: '',
  pseudo: '',
  phoneNumber: ''
});
const errors = reactive({
  name: '',
  phoneNumber: ''
});

// Methods
function showModal() {
  modal.value?.show();
}

function hideModal() {
  modal.value?.hide();
}

function validateForm() {
  let isValid = true;

  // Reset errors
  errors.name = '';
  errors.phoneNumber = '';

  // Validate name
  if (!form.name.trim()) {
    errors.name = 'Le nom est requis';
    isValid = false;
  }

  // Validate phone
  if (!form.phoneNumber.trim()) {
    errors.phoneNumber = 'Le telephone est requis';
    isValid = false;
  }

  return isValid;
}

function submitForm() {
  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;

  const requestData = {
    name: form.name.trim(),
    pseudo: form.pseudo.trim(),
    phoneNumber: form.phoneNumber.trim()
  };

  bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATE, requestData);
}

function copyLink() {
  if (generatedLink.value) {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(generatedLink.value)
        .then(() => {
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Lien copie',
            body: 'Le lien a ete copie dans le presse-papier'
          });
        })
        .catch(() => {
          fallbackCopyToClipboard();
        });
    } else {
      fallbackCopyToClipboard();
    }
  }
}

function fallbackCopyToClipboard() {
  const textArea = document.createElement('textarea');
  textArea.value = generatedLink.value;
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();
  try {
    document.execCommand('copy');
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'success',
      title: 'Lien copie',
      body: 'Le lien a ete copie dans le presse-papier'
    });
  } catch (err) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Impossible de copier le lien'
    });
  }
  document.body.removeChild(textArea);
}

function resetForm() {
  form.name = '';
  form.pseudo = '';
  form.phoneNumber = '';
  generatedLink.value = '';
  errors.name = '';
  errors.phoneNumber = '';
  isSubmitting.value = false;
}

// Listen for request created event
function onRequestCreated(data) {
  console.log('[DeliveryBookRequestDialog] Request created:', data);

  // Extract the link from the response
  if (data.request?.locationRequestLink) {
    generatedLink.value = data.request.locationRequestLink;
  } else if (data.address?.locationRequestLink) {
    generatedLink.value = data.address.locationRequestLink;
  }

  isSubmitting.value = false;
}

// Listen for request error
function onRequestError(error) {
  console.error('[DeliveryBookRequestDialog] Request error:', error);
  isSubmitting.value = false;
}

// Listen for tab changes to close modal
function onTabChanged(tab) {
  if (tab === 'requests') {
    // Keep modal open to show the newly created request
    resetForm();
  }
}

onMounted(() => {
  bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATED, onRequestCreated);
  bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATE_ERROR, onRequestError);
  bus.on(constants.EVENTS.DELIVERYBOOK.TAB_CHANGED, onTabChanged);
});

onBeforeUnmount(() => {
  bus.off(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATED, onRequestCreated);
  bus.off(constants.EVENTS.DELIVERYBOOK.ADDRESS_REQUEST_CREATE_ERROR, onRequestError);
  bus.off(constants.EVENTS.DELIVERYBOOK.TAB_CHANGED, onTabChanged);
});
</script>
